import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Returning from Google OAuth? Skip the /me check — AuthCallback establishes the session first.
    if (window.location.hash && window.location.hash.includes("session_id=")) { setReady(true); return; }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const persist = (data) => {
    // Auth token lives only in the server-set httpOnly cookie (not JS-readable) to prevent XSS token theft.
    setUser(data.user);
  };

  const login = async (email, password, turnstile_token) => {
    const { data } = await api.post("/auth/login", { email, password, turnstile_token });
    if (data.two_factor_required) return data; // caller drives OTP step
    persist(data);
    return data.user;
  };

  const verify2fa = async (user_id, code) => {
    const { data } = await api.post("/auth/verify-2fa", { user_id, code });
    persist(data);
    return data.user;
  };

  const requestEmailCode = async (email, turnstile_token) => {
    const { data } = await api.post("/auth/email-code/request", { email, turnstile_token });
    return data;
  };

  const loginWithEmailCode = async (email, code) => {
    const { data } = await api.post("/auth/email-code/verify", { email, code });
    if (data.two_factor_required) return data; // caller drives OTP step
    persist(data);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persist(data);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    setUser(false);
  };

  const refreshUser = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, ready, login, verify2fa, requestEmailCode, loginWithEmailCode, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
